import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { VpError } from '../../../generated/internal.graphql';
import * as VPError from '../../../VPError';
import { type InternalApiError, toApiErrors } from '../errors';
import type { VPResult } from '../../VPResult';
import * as Category from './Category';
import * as Subcategory from './Subcategory';
import * as Type from './Type';
import {
  INTERNAL_CATEGORY_FIELDS,
  INTERNAL_SUBCATEGORY_FIELDS,
  INTERNAL_TYPE_FIELDS,
} from './fragments';
import { ACCOUNT_AGNOSTIC } from '../../accountContext';

/**
 * The genre taxonomy — event types, categories and their subcategories — as VenuePilot staff
 * administer it.
 *
 * This is platform-wide reference data: one taxonomy every account picks from, not per-account
 * settings. It used to live on AppSchema (`x-vp-api: admin`) as root `types` / `categories` plus
 * `saveType` / `saveCategory` / `saveSubcategory`, all behind `require_superadmin!`; ADR-009 moved
 * it to InternalSchema and removed it from AppSchema, because a superadmin-only surface has no
 * business in a schema published to every account holder.
 *
 * Auth is the superadmin Rails session cookie — a bearer token never satisfies the gate, so this
 * only works in the Rails-embedded admin against a same-origin `/graphql`, never the standalone dev
 * server. Everything here goes through `internalGqlApiClient`.
 *
 * A non-superadmin caller is rejected with HTTP 401 before the query is parsed, which Apollo
 * *throws* rather than returning as a payload. Every function below therefore catches and
 * classifies via `toApiErrors`, so callers can branch on `isUnauthorized` and say "VenuePilot staff
 * only" instead of rendering an empty catalog — and so an expected authorization outcome does not
 * reach Sentry as an exception. **Nothing here throws.**
 *
 * Two result shapes, matching `Internal/TicketLayout/Api.ts` and `Internal/AppConfig/Api.ts`: the
 * read resolves `VPResult`, the mutations resolve `MutationResult` plus the entity they saved, so
 * no caller needs a follow-up refetch.
 */

export type MutationResult = { success: boolean; errors: InternalApiError[] };

export type Genres = {
  types: Type.Type[];
  categories: Category.Category[];
};

const INTERNAL_GENRES_QUERY = gql`
  query InternalGenres {
    types {
      ...InternalTypeFields
    }
    categories {
      ...InternalCategoryFields
    }
  }
  ${INTERNAL_TYPE_FIELDS}
  ${INTERNAL_CATEGORY_FIELDS}
`;

/**
 * The three mutations are RelayClassic, so the arguments are wrapped in `input:` inside the
 * document and the variable carries the attributes alone — the same shape as
 * `InternalSaveTicketLayout`, and it keeps the caller from having to know about `input`.
 *
 * None of them passes `id`. `TypeAttributes` / `CategoryAttributes` / `SubcategoryAttributes` all
 * accept an optional one, and passing it is what turns a create into an update — but these screens
 * only add. Editing an existing record additionally needs the backend's
 * `categorisation_editing_enabled` system setting, whose absence comes back as a *top-level* error
 * (so through `toApiErrors`), not in the payload's `errors`.
 */
const INTERNAL_SAVE_TYPE_MUTATION = gql`
  mutation InternalSaveType($type: TypeAttributes!) {
    saveType(input: { type: $type }) {
      type {
        ...InternalTypeFields
      }
      errors {
        message
        path
      }
    }
  }
  ${INTERNAL_TYPE_FIELDS}
`;

const INTERNAL_SAVE_CATEGORY_MUTATION = gql`
  mutation InternalSaveCategory($category: CategoryAttributes!) {
    saveCategory(input: { category: $category }) {
      category {
        ...InternalCategoryFields
      }
      errors {
        message
        path
      }
    }
  }
  ${INTERNAL_CATEGORY_FIELDS}
`;

const INTERNAL_SAVE_SUBCATEGORY_MUTATION = gql`
  mutation InternalSaveSubcategory($subcategory: SubcategoryAttributes!) {
    saveSubcategory(input: { subcategory: $subcategory }) {
      subcategory {
        ...InternalSubcategoryFields
      }
      errors {
        message
        path
      }
    }
  }
  ${INTERNAL_SUBCATEGORY_FIELDS}
`;

const toErrors = (errors?: VpError[] | null): InternalApiError[] =>
  VPError.fromGqlCollection(errors ?? []);

/**
 * A failed mutation with an empty `errors` list leaves the caller nothing to render. Today's
 * resolvers always populate one — but a null payload does not, so fall back rather than report a
 * silent failure.
 */
const failed = (errors: InternalApiError[], fallback: string): MutationResult => ({
  success: false,
  errors: errors.length ? errors : [{ path: '', message: fallback }],
});

/**
 * `network-only` is about a fresh mount, not about saves: the screens fold each mutation's own
 * payload into local state and never re-read. Serving a cached catalog would show an operator the
 * taxonomy as it stood when they last opened the screen, after another staff member has changed it.
 *
 * `types` and `categories` are both non-null on the schema, so the `?? []` guards are the whole
 * story — there is no nullable connection here to report as a backend fault.
 */
export const fetchGenres = async (
  client: ApolloClient<NormalizedCacheObject>,
): Promise<VPResult<Genres, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_GENRES_QUERY,
      fetchPolicy: 'network-only',
      // Account-agnostic: the platform-wide categorisation catalog. No account owns a type or a
      // category.
      context: ACCOUNT_AGNOSTIC,
    });

    return {
      success: true,
      data: {
        types: (data?.types ?? []).map(Type.fromGql),
        categories: (data?.categories ?? []).map(Category.fromGql),
      },
    };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const saveType = async (
  client: ApolloClient<NormalizedCacheObject>,
  name: string,
): Promise<MutationResult & { type?: Type.Type }> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_SAVE_TYPE_MUTATION,
      variables: { type: { name } },
      fetchPolicy: 'network-only',
      // Account-agnostic: a global catalog row.
      context: ACCOUNT_AGNOSTIC,
    });

    const errors = toErrors(data?.saveType?.errors);
    const saved = data?.saveType?.type;

    if (errors.length || !saved) {
      return failed(errors, 'Failed to save the type.');
    }

    return { success: true, errors: [], type: Type.fromGql(saved) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const saveCategory = async (
  client: ApolloClient<NormalizedCacheObject>,
  name: string,
): Promise<MutationResult & { category?: Category.Category }> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_SAVE_CATEGORY_MUTATION,
      variables: { category: { name } },
      fetchPolicy: 'network-only',
      // Account-agnostic: a global catalog row.
      context: ACCOUNT_AGNOSTIC,
    });

    const errors = toErrors(data?.saveCategory?.errors);
    const saved = data?.saveCategory?.category;

    if (errors.length || !saved) {
      return failed(errors, 'Failed to save the category.');
    }

    return { success: true, errors: [], category: Category.fromGql(saved) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const saveSubcategory = async (
  client: ApolloClient<NormalizedCacheObject>,
  categoryId: string,
  name: string,
): Promise<MutationResult & { subcategory?: Subcategory.Subcategory }> => {
  try {
    const { data } = await client.mutate({
      mutation: INTERNAL_SAVE_SUBCATEGORY_MUTATION,
      variables: { subcategory: { categoryId, name } },
      fetchPolicy: 'network-only',
      // Account-agnostic: a global catalog row.
      context: ACCOUNT_AGNOSTIC,
    });

    const errors = toErrors(data?.saveSubcategory?.errors);
    const saved = data?.saveSubcategory?.subcategory;

    if (errors.length || !saved) {
      return failed(errors, 'Failed to save the subcategory.');
    }

    return { success: true, errors: [], subcategory: Subcategory.fromGql(saved) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};
